// src/api/users-api.js - Updated with real database queries for regions, villes, and pharmacies
import { gql } from '@apollo/client';
import client from './apollo-client';

// ✅ EXPORT GraphQL queries so they can be imported in components
export const GET_USERS_PAGINATED = gql`
  query GetUsersPaginated($usersQueryInput: UsersQueryInput) {
    usersPaginated(usersQueryInput: $usersQueryInput) {
      users {
        id
        email
        firstName
        lastName
        tel
        CIN
        RIB
        address
        userType
        deviceType
        commissionBalance
        approvedCommission
        waitingCommission
        isActive
        isPhoneVerified
        lastLogin
        createdAt
        updatedAt
        pharmacy {
          id
          namePharmacy
          telPharmacy
          commissionBalance
          regionId
          villeId
          region {
            id
            nomRegion
          }
          ville {
            id
            nomVilleVillage
          }
        }
        ownedLaboratory {
          id
          nom
        }
      }
      pagination {
        currentPage
        totalPages
        totalItems
        itemsPerPage
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

export const GET_USER_STATISTICS = gql`
  query GetUserStatistics($userIds: [String!]!) {
    userStatistics(userIds: $userIds)
  }
`;

export const GET_USERS_OVERALL_STATS = gql`
  query GetUsersOverallStats {
    usersStatistics {
      total
      active
      inactive
      verified
      unverified
      byType {
        ADMIN
        PHARMACIST
        LABORATORY
        SELLER
      }
    }
  }
`;

// ✅ NEW: Real GraphQL queries for regions, villes, and pharmacies
export const GET_REGIONS = gql`
  query GetRegions {
    regions {
      id
      nomRegion
      createdAt
      updatedAt
    }
  }
`;

export const GET_VILLES_BY_REGION = gql`
  query GetVillesByRegion($regionId: ID!) {
    villesByRegion(regionId: $regionId) {
      id
      nomVilleVillage
      regionId
      createdAt
      updatedAt
    }
  }
`;

export const GET_ALL_VILLES = gql`
  query GetAllVilles {
    villes {
      id
      nomVilleVillage
      regionId
      createdAt
      updatedAt
      region {
        id
        nomRegion
      }
    }
  }
`;

export const GET_PHARMACIES = gql`
  query GetPharmacies {
    pharmacies {
      id
      namePharmacy
      telPharmacy
      commissionBalance
      regionId
      villeId
      createdAt
      updatedAt
      region {
        id
        nomRegion
      }
      ville {
        id
        nomVilleVillage
      }
    }
  }
`;

export const GET_PHARMACIES_BY_REGION = gql`
  query GetPharmaciesByRegion($regionId: ID!) {
    pharmaciesByRegion(regionId: $regionId) {
      id
      namePharmacy
      telPharmacy
      commissionBalance
      regionId
      villeId
      createdAt
      updatedAt
      ville {
        id
        nomVilleVillage
      }
    }
  }
`;

// ✅ GraphQL mutations matching the actual resolver signatures
const TOGGLE_USER_ACTIVE = gql`
  mutation ToggleUserActive($id: ID!, $isActive: Boolean!) {
    toggleUserActive(id: $id, isActive: $isActive) {
      id
      email
      firstName
      lastName
      isActive
      userType
      lastLogin
    }
  }
`;

const UPDATE_USER = gql`
  mutation UpdateUser(
    $id: ID!
    $tel: String
    $address: String
    $CIN: String
    $RIB: String
    $isActive: Boolean
    $isPhoneVerified: Boolean
    $firstName: String
    $lastName: String
  ) {
    updateUser(
      id: $id
      tel: $tel
      address: $address
      CIN: $CIN
      RIB: $RIB
      isActive: $isActive
      isPhoneVerified: $isPhoneVerified
      firstName: $firstName
      lastName: $lastName
    ) {
      id
      email
      firstName
      lastName
      tel
      userType
      isActive
      isPhoneVerified
      commissionBalance
      lastLogin
      createdAt
      updatedAt
    }
  }
`;

const CREATE_USER = gql`
  mutation CreateUser(
    $email: String!
    $password: String!
    $userType: String!
    $firstName: String
    $lastName: String
    $tel: String
  ) {
    createUser(
      email: $email
      password: $password
      userType: $userType
      firstName: $firstName
      lastName: $lastName
      tel: $tel
    ) {
      id
      email
      firstName
      lastName
      tel
      userType
      isActive
      isPhoneVerified
      commissionBalance
      createdAt
      updatedAt
    }
  }
`;

const DELETE_USER = gql`
  mutation DeleteUser($id: ID!) {
    deleteUser(id: $id)
  }
`;

export const ADMIN_SET_PASSWORD = gql`
  mutation AdminSetPassword($id: ID!, $newPassword: String!) {
    adminSetPassword(id: $id, newPassword: $newPassword)
  }
`;

// ✅ Users API object with all methods using real database queries
export const usersApi = {
  // ✅ REAL: Get regions from database
  getRegions: async () => {
    try {
      const { data } = await client.query({
        query: GET_REGIONS,
        fetchPolicy: 'cache-first'
      });
      return data.regions || [];
    } catch (error) {
      console.error('Error fetching regions:', error);
      // Fallback to empty array if query fails
      return [];
    }
  },

  // ✅ REAL: Get all pharmacies from database
  getPharmacies: async () => {
    try {
      const { data } = await client.query({
        query: GET_PHARMACIES,
        fetchPolicy: 'cache-first'
      });
      return data.pharmacies || [];
    } catch (error) {
      console.error('Error fetching pharmacies:', error);
      // Fallback to empty array if query fails
      return [];
    }
  },

  // ✅ REAL: Get pharmacies by region from database
  getPharmaciesByRegion: async (regionId) => {
    try {
      const { data } = await client.query({
        query: GET_PHARMACIES_BY_REGION,
        variables: { regionId },
        fetchPolicy: 'cache-first'
      });
      return data.pharmaciesByRegion || [];
    } catch (error) {
      console.error('Error fetching pharmacies by region:', error);
      return [];
    }
  },

  // ✅ REAL: Get villes by region from database
  getVillesByRegion: async (regionId) => {
    try {
      const { data } = await client.query({
        query: GET_VILLES_BY_REGION,
        variables: { regionId },
        fetchPolicy: 'cache-first'
      });
      return data.villesByRegion || [];
    } catch (error) {
      console.error('Error fetching villes by region:', error);
      return [];
    }
  },

  // ✅ REAL: Get all villes from database
  getAllVilles: async () => {
    try {
      const { data } = await client.query({
        query: GET_ALL_VILLES,
        fetchPolicy: 'cache-first'
      });
      return data.villes || [];
    } catch (error) {
      console.error('Error fetching all villes:', error);
      return [];
    }
  },

  // ✅ FIXED: Toggle user active status with correct GraphQL mutation
  toggleUserActive: async (userId, isActive) => {
    try {
      console.log(`Toggling user ${userId} active status to ${isActive}`);
      
      const { data } = await client.mutate({
        mutation: TOGGLE_USER_ACTIVE,
        variables: {
          id: userId,
          isActive: isActive
        },
        // Refetch queries to update the cache
        refetchQueries: [
          'GetUsersPaginated',
          'GetUsersOverallStats'
        ],
        // Also update the cache optimistically
        optimisticResponse: {
          toggleUserActive: {
            __typename: 'User',
            id: userId,
            isActive: isActive,
            email: '',
            firstName: null,
            lastName: null,
            userType: 'PHARMACIST',
            lastLogin: null
          }
        }
      });

      console.log('Toggle user active mutation successful:', data);
      return data.toggleUserActive;
    } catch (error) {
      console.error(`Toggle user active ${userId} error:`, error);
      
      // Log GraphQL errors for debugging
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        console.error('GraphQL Errors:', error.graphQLErrors);
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      
      throw error;
    }
  },

  // Create user
  createUser: async (userData) => {
    try {
      const { data } = await client.mutate({
        mutation: CREATE_USER,
        variables: userData,
        refetchQueries: [
          'GetUsersPaginated',
          'GetUsersOverallStats'
        ]
      });
      return data.createUser;
    } catch (error) {
      console.error('Create user error:', error);
      throw error;
    }
  },

  // Update user
  updateUser: async (userData) => {
    try {
      const { data } = await client.mutate({
        mutation: UPDATE_USER,
        variables: userData,
        refetchQueries: [
          'GetUsersPaginated'
        ]
      });
      return data.updateUser;
    } catch (error) {
      console.error(`Update user ${userData.id} error:`, error);
      throw error;
    }
  },

  // Delete user
  deleteUser: async (userId) => {
    try {
      const { data } = await client.mutate({
        mutation: DELETE_USER,
        variables: { id: userId },
        refetchQueries: [
          'GetUsersPaginated',
          'GetUsersOverallStats'
        ]
      });
      return data.deleteUser;
    } catch (error) {
      console.error(`Delete user ${userId} error:`, error);
      throw error;
    }
  },

  // Send verification SMS (mock implementation)
  sendVerificationSMS: async (userId, phoneNumber) => {
    try {
      // This would need to be implemented in your backend
      // For now, we'll simulate an API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      return { success: true, message: 'SMS de vérification envoyé' };
    } catch (error) {
      console.error(`Send verification SMS to ${phoneNumber} error:`, error);
      throw error;
    }
  },

  // Helper functions for data transformation
  transformUserForDisplay: (user) => {
    return {
      ...user,
      regionName: user.pharmacy?.region?.nomRegion || null,
      villeName: user.pharmacy?.ville?.nomVilleVillage || null,
      pharmacyName: user.pharmacy?.namePharmacy || user.ownedLaboratory?.nom || null,
      regionId: user.pharmacy?.regionId || null,
      villeId: user.pharmacy?.villeId || null,
      pharmacyId: user.pharmacy?.id || null,
      laboratoryId: user.ownedLaboratory?.id || null
    };
  },

  // Statistics helpers
  getUserStatistics: (users) => {
    return {
      total: users.length,
      active: users.filter(u => u.isActive).length,
      inactive: users.filter(u => !u.isActive).length,
      verified: users.filter(u => u.isPhoneVerified).length,
      unverified: users.filter(u => !u.isPhoneVerified).length,
      byType: {
        ADMIN: users.filter(u => u.userType === 'ADMIN').length,
        PHARMACIST: users.filter(u => u.userType === 'PHARMACIST').length,
        LABORATORY: users.filter(u => u.userType === 'LABORATORY').length,
        SELLER: users.filter(u => u.userType === 'SELLER').length
      }
    };
  }
};

// ✅ Default export for backward compatibility
export default usersApi;